// Pure pieces of experiment assignment, kept apart from React so they can be
// tested on their own. See useExperimentVariant for how they fit together.

export const CONTROL = 'control';

/** The variant to render for a raw value; anything unknown is control. */
export const pickVariant = (
  value: string | null | undefined,
  variants: Record<string, unknown>
): string =>
  value && Object.prototype.hasOwnProperty.call(variants, value)
    ? value
    : CONTROL;

/**
 * The decision ExperimentRoute makes once per mount, without the side effects:
 * undefined to keep waiting, otherwise the variant and whether this is a fresh
 * assignment (lock it, report the exposure). Order matters — see
 * useExperimentVariant.
 */
export const decideVariant = ({
  eligible,
  locked,
  flagsStatus,
  flagValue,
  variants
}: {
  eligible: boolean | undefined;
  locked: string | null;
  flagsStatus: 'waiting' | 'ready' | 'timeout';
  flagValue: string;
  variants: Record<string, unknown>;
}): { variant: string; fresh: boolean } | undefined => {
  if (eligible === undefined) return undefined;
  if (!eligible) return { variant: CONTROL, fresh: false };
  if (locked) return { variant: pickVariant(locked, variants), fresh: false };
  if (flagsStatus === 'waiting') return undefined;
  // Slow load: show control, but keep it out of the experiment.
  if (flagsStatus === 'timeout') return { variant: CONTROL, fresh: false };
  return { variant: pickVariant(flagValue, variants), fresh: true };
};

/**
 * QA override from the URL: `?exp=<experiment key>:<variant>`. Repeat the
 * param to force several experiments at once. Null when this experiment isn't
 * named.
 */
export const parseOverride = (search: string, key: string): string | null => {
  const match = new URLSearchParams(search)
    .getAll('exp')
    .map((param) => param.split(':'))
    .find(([name]) => name === key);
  return match?.[1] ?? null;
};

// The variant a user got is locked for the rest of the session, so a flag
// value that lands or changes later can never swap the screen, or mix
// variants across the screens of one flow. sessionStorage can throw (private
// mode, blocked storage); without it the user is simply re-assigned per load.
const lockKey = (key: string) => `exp:${key}`;

export const readLock = (key: string): string | null => {
  try {
    return sessionStorage.getItem(lockKey(key));
  } catch {
    return null;
  }
};

export const writeLock = (key: string, variant: string): void => {
  try {
    sessionStorage.setItem(lockKey(key), variant);
  } catch {
    // see above
  }
};

/** Drop every experiment lock (logout), so the next user is assigned afresh. */
export const clearExperimentLocks = (): void => {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('exp:'))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // see readLock
  }
};
